/**
 * Created by rathompson on 11/30/21
 * File: TreeSearch
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useEffect, useState } from 'react'
import { LABEL_NO_RESULTS, LABEL_SEARCH_RESULTS, LABEL_SHOW_ALL_RESULTS } from '../../../../tenantSwitcher/constants/LabelConstants'
import { Body, Bold, Button, Tooltip, noop } from '@panwds/react-ui'
import { DATA_TEST_SEARCH_TREE_BUTTON } from '../../../constants/TestingDataIds'
import type { ReactNode, VFC } from 'react'
import type { Tsg } from '@sparky/framework'

const SEARCH_RESULT_LIMIT = 100

export type TsgSearchResult = {
  tenant: Tsg
  parent?: Tsg
  tooltip: ReactNode
}
export const searchTenant = (searchInput: string, tenant: Tsg): TsgSearchResult[] => {
  const input = searchInput.trim().toLowerCase()
  const array = [] as TsgSearchResult[]
  const searchRecursive = (tenant: Tsg, parent?: Tsg, tooltip: string = '') => {
    const str = `${tooltip ? `${tooltip} > ` : ''}${parent ? `${parent?.display_name} > ${tenant?.display_name}` : tenant?.id !== '_superRoot' ? tenant?.display_name : ''}`
    if (tenant?.display_name?.toLowerCase().includes(input) || String(tenant?.id).startsWith(input)) {
      if (tenant.id !== '_superRoot') {
        array?.push({
          tenant,
          parent,
          tooltip: <>{str} <small className='cm-opacity-50'>- {tenant.id}</small></>
        })
      }
    }
    if (tenant?.children?.length) {
      for (const child of tenant.children) {
        searchRecursive(child, tenant, str)
      }
    }
  }
  searchRecursive(tenant)
  return array
}

/**
 * Tree search results
 */
export type TreeSearchProps = {
  searchInput: string
  searchResults: TsgSearchResult[]
  onResultClick: (tenant: Tsg) => void
  setOutsideId?: (tsg_id: string) => void
}

export const TreeSearch: VFC<TreeSearchProps> = ({ searchInput, searchResults, onResultClick, setOutsideId = noop }) => {
  const [showAll, setShowAll] = useState(false)
  const clickToShowAll = useCallback(() => {
    setShowAll(true)
  }, [])
  /**
   *     ========= Callback ===========
   */
  const onClick = useCallback((tenant: any) => {
    onResultClick(tenant)
  }, [onResultClick])

  const highlight = useCallback((text?: string, id?: string) => {
    if (!text) {
      return null
    }
    const search = searchInput.trim().toLowerCase()
    const foundIdx = text.toLowerCase().indexOf(search)
    if (foundIdx < 0) {
      if (id && /^\d+$/.test(search)) {
        const foundTidIdx = id.indexOf(search)
        if (foundTidIdx >= 0) {
          const toIdx = foundTidIdx + search.length
          const highId = <small>- {
            id.slice(0, foundTidIdx)
          }<mark className='tw-bg-blue-200 hover:tw-text-blue-700 tw-text-blue-600 dark:tw-bg-gray-600 dark:tw-text-blue-200'>{
            id.slice(foundTidIdx, toIdx)
          }</mark>{id.slice(toIdx)}</small>
          return <Bold appearance='semibold' addClassName='tw-p-4'>{text} {highId}</Bold>
        }
      }
      return <Bold appearance='semibold' addClassName='tw-p-4'>{text}</Bold>
    }
    const toIdx = foundIdx + search.length
    return <Bold appearance='semibold' addClassName='tw-p-4'>{
      text.slice(0, foundIdx)
    }<mark className='tw-bg-blue-200 hover:tw-text-blue-700 tw-text-blue-600 dark:tw-bg-gray-600 dark:tw-text-blue-200'>{
      text.slice(foundIdx, toIdx)
    }</mark>{text.slice(toIdx)}</Bold>
  }, [searchInput])

  useEffect(() => {
    setShowAll(false)
  }, [searchInput])

  if (!searchInput) {
    return null
  }

  const filteredResults = searchResults.length && !showAll ? searchResults.slice(0, SEARCH_RESULT_LIMIT) : searchResults

  return (
    <div className='tw-h-full tw-w-full tw-overflow-auto tw-scrollbar-default dark:tw-scrollbar-track-dark dark:tw-scrollbar-thumb-dark dark:tw-scrollbar-corner-dark'>
      <div className='tw-p-4'>
        <Body addClassName='tw-text-gray-600' size='lg'>{LABEL_SEARCH_RESULTS}</Body>
      </div>
      <div className='tw-mt-4'>
        {filteredResults.length ? filteredResults.map(({ tenant, parent, tooltip }, index) => {
          return (
            <div key={index} className='tw-mt-4 tw-truncate'
              // hovering setOutsideTsgId
              onMouseEnter={() => {
                setOutsideId(tenant.id)
              }}
              onMouseLeave={() => {
                setOutsideId('')
              }}
            >
              {parent && <Body size='lg' addClassName='tw-pl-4 tw-pr-4 tw-text-gray-600'>{parent.display_name}</Body>}
              <Tooltip label={tooltip}>
                <div
                  data-testid={DATA_TEST_SEARCH_TREE_BUTTON}
                  data-tsg-id={tenant.id}
                  className='tw-text-base tw-cursor-pointer hover:tw-bg-blue-100 hover:tw-text-blue-700 tw-text-blue-600 tw-truncate dark:hover:cm-bg-gray-900'
                  onClick={onClick.bind(this, tenant)}
                >
                  {highlight(tenant.display_name, tenant.id)}
                </div>
              </Tooltip>
            </div>
          )
        }) : (
          <div className='tw-p-4 tw-text-gray-600'>
            {LABEL_NO_RESULTS}
          </div>
        )}
        {!showAll && searchResults.length > SEARCH_RESULT_LIMIT &&
          <div className='tw-p-4 tw-text-gray-600'>
            <Button appearance='tertiary-clear' onClick={clickToShowAll} addClassName='cm-w-full'>{LABEL_SHOW_ALL_RESULTS.replace('{0}', String(searchResults.length))}</Button>
          </div>
        }
      </div>
    </div>
  )
}

export default TreeSearch
